import Button from '@ingka/button';
import chevronDownSmall from '@ingka/ssr-icon/paths/chevron-down-small';
import chevronUpSmall from '@ingka/ssr-icon/paths/chevron-up-small';
import useApp from 'hooks/useApp';
import { getCostCentreDescription } from 'hooks/useGrid/gridFunctions';
import moment from 'moment';
import { useState } from 'react';
import { DATE_FORMAT_FULL_DAY_SHORT_MONTH_FULL_YEAR } from 'utils/date';
import { TypeCoworkerActionPlan } from 'types/actionPlan';
import { getShortCC } from 'utils/text';
import Text from '@ingka/text';
import { useNestedTranslation } from 'hooks/useNestedTranslation';
import classes from './ActionTable.module.scss';
import ActionTableCoworkerDetails from './ActionTableCoworkerDetails';

export const CapacityActionTableRow = ({
    coworkerActionPlan,
}: {
    coworkerActionPlan: TypeCoworkerActionPlan
}) => {
    const { orgSelection: { utils: { hierarchy } } } = useApp();
    const t = useNestedTranslation('actionPlan.table');
    const [showDetails, setShowDetails] = useState<boolean>(false);
    const { name, isDummy, costCentres, lastChanged, actionGroups, personId } = coworkerActionPlan;
    const totalActions = actionGroups.reduce((accumulator, currentValue) => accumulator + currentValue.actions.length, 0);

    const handleClick = () => {
        setShowDetails(!showDetails);
    };

    return (
        <>
            <tr>
                <td>
                    <div
                        className={classes['table__name-cell']}
                    >
                        <Button
                            data-testid="capacity-action-plan-button"
                            iconPosition="trailing"
                            iconOnly
                            size="small"
                            ssrIcon={showDetails ? chevronUpSmall : chevronDownSmall}
                            type="tertiary"
                            onClick={handleClick}
                            // Using text with iconOnly for accessibility
                            text={showDetails ? t('hideDetails') : t('showDetails')}
                        />
                        <div className={classes.table__coworker}>
                            <Text
                                textStyle="Heading.XS"
                                data-testid="action-plan-row-name"
                            >
                                {name}
                                {' '}
                                {isDummy && `(${t.global('new').toUpperCase()})`}
                            </Text>
                            {!isDummy ? (
                                <Text data-testid="action-plan-row-personId" textStyle="Caption.M">
                                    {personId}
                                </Text>
                            )
                                : ''}
                        </div>
                    </div>
                </td>
                <td>
                    <Text textStyle="Heading.XS" data-testid="action-plan-row-num-changes">
                        {t('numChanges', { count: totalActions })}
                    </Text>
                </td>
                <td>
                    <Text textStyle="Heading.XS" data-testid="action-plan-row-change-date">
                        {moment(lastChanged).format(DATE_FORMAT_FULL_DAY_SHORT_MONTH_FULL_YEAR)}
                    </Text>
                </td>
                <td>
                    <Text data-testid="action-plan-row-all-cost-centres">
                        {costCentres.map((cc, index) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <div key={`costDistributions-${index}`}>
                                <Text textStyle="Heading.XS" data-testid="action-plan-row-cost-centre-desc">
                                    {getCostCentreDescription(cc, hierarchy?.costCentreHierarchy)}
                                </Text>
                                <Text textStyle="Body.M" data-testid="action-plan-row-cost-centre-id">
                                    {`CC${getShortCC(cc)}`}
                                </Text>
                            </div>
                        ))}
                    </Text>
                </td>
            </tr>
            {showDetails && (
            <tr>
                <td colSpan={100}>
                    <ActionTableCoworkerDetails coworkerActionPlan={coworkerActionPlan} />
                </td>
            </tr>
            )}
        </>
    );
};
